import React from 'react';

import {
  fetchContentList,
  getCachedContentList,
  getStaleContentList,
  subscribeToCmsChanges
} from '../api/cms';
import { useLanguage } from '../context/LanguageContext';

export function useContentList(resource, options = {}) {
  const { currentLanguageCode } = useLanguage();
  const optionsKey = JSON.stringify(options || {});
  const parsedOptions = React.useMemo(() => JSON.parse(optionsKey), [optionsKey]);
  const enabled = parsedOptions.enabled !== false;
  const shouldRevalidate = parsedOptions.revalidate !== false;
  const query = React.useMemo(() => {
    const { enabled: _enabled, revalidate: _revalidate, ...rest } = parsedOptions;

    if (resource === 'faculty-page-menus') {
      return {
        activeOnly: true,
        imageMode: 'separate',
        summaryOnly: true,
        compact: true,
        ...rest
      };
    }

    return rest;
  }, [parsedOptions, resource]);
  const cachedItems = React.useMemo(
    () => (enabled ? getCachedContentList(resource, query) : null),
    [enabled, query, resource]
  );
  // Oldingi tashrifdan qolgan nusxa (faqat banner kabi kichik ro'yxatlar uchun saqlanadi).
  const staleItems = React.useMemo(
    () => (enabled && !cachedItems ? getStaleContentList(resource, query) : null),
    [cachedItems, enabled, query, resource]
  );
  const [items, setItems] = React.useState(() => cachedItems || staleItems || []);
  const [loading, setLoading] = React.useState(() => enabled && !cachedItems && !staleItems);

  React.useEffect(() => {
    let cancelled = false;

    const load = async (forceRefresh = false) => {
      if (!enabled) {
        setItems([]);
        setLoading(false);
        return;
      }

      try {
        if (cachedItems && !forceRefresh) {
          setItems(cachedItems);
          setLoading(false);
          if (!shouldRevalidate) {
            return;
          }
        }

        setLoading((current) => current || !staleItems);
        const nextItems = await fetchContentList(resource, query, {
          // Cached content is rendered immediately; only a CMS revision event needs a forced refresh.
          force: forceRefresh
        });

        if (!cancelled) {
          setItems(nextItems);
        }
      } catch (error) {
        // Tarmoq xatosida ko'rsatilayotgan ro'yxatni o'chirmaymiz.
        console.error(`Failed to load ${resource}:`, error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    const unsubscribe = shouldRevalidate
      ? subscribeToCmsChanges(() => {
          load(true);
        })
      : () => {};

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [cachedItems, enabled, query, resource, shouldRevalidate, staleItems]);

  return { items, loading, setItems, currentLanguageCode };
}
